import React from 'react';
import ClubCardBase, { ClubCardHeader, ClubCardStats, ClubCardAction } from './ClubCardBase';
import ClubBanner from './ClubBanner';
import { ClubPersonalInsights, getClubRoleBadgeClass, getClubRoleLabel } from './ClubPersonalInsights';
import { getPlayerAvatarPublicUrl } from '../../services/avatarService';

/**
 * ClubAdmins - Display club admin avatars
 */
const ClubAdmins = ({ admins = [], compact = false }) => {
  if (!admins || admins.length === 0) return null;

  const sizeClass = compact ? 'w-6 h-6' : 'w-8 h-8';
  const textClass = compact ? 'text-[0.6rem]' : 'text-xs';

  return (
    <div className={`flex items-center gap-2 ${compact ? '' : 'pt-3 mt-3 border-t border-border'}`}>
      {!compact ? (
        <span className="text-xs text-text-secondary">Club Admins:</span>
      ) : null}
      <div className="flex items-center -space-x-1.5">
        {admins.slice(0, 3).map((admin, index) => (
          <div
            key={admin.id}
            className={`${sizeClass} rounded-full border-2 border-surface bg-gradient-to-br from-secondary to-primary-dark flex items-center justify-center overflow-hidden relative`}
            style={{ zIndex: 3 - index }}
            title={`${admin.first_name} ${admin.last_name}`}
          >
            {admin.avatar_url ? (
              <img
                src={getPlayerAvatarPublicUrl(admin.avatar_url)}
                alt={`${admin.first_name} ${admin.last_name}`}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className={`${textClass} font-semibold text-white`}>
                {admin.first_name?.charAt(0)}{admin.last_name?.charAt(0)}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const ClubCardMetaRow = ({ memberCount, admins, compact = false }) => {
  if (memberCount == null && (!admins || admins.length === 0)) return null;

  return (
    <div className={`flex items-center justify-between gap-3 ${compact ? 'pt-2' : 'pt-3 mt-3 border-t border-border'}`}>
      {memberCount != null ? (
        <span className="text-xs text-text-secondary tabular-nums shrink-0">
          {memberCount} {memberCount === 1 ? 'member' : 'members'}
        </span>
      ) : (
        <span />
      )}
      {admins?.length > 0 ? <ClubAdmins admins={admins} compact={compact} /> : null}
    </div>
  );
};

/**
 * ClubCard - Main component for displaying club cards
 * Supports multiple variants (member, public, etc.) through composition
 * 
 * @example
 * // Member variant (user belongs to club)
 * <ClubCard
 *   variant="member"
 *   club={{ id: '1', name: 'My Club', location: 'London', role: 'member', banner_image_url: 'club-uuid.jpg' }}
 *   stats={{ mixedElo: 1200, levelsElo: 1200, totalGamesPlayed: 50 }}
 *   onClick={() => navigate(`/profile/clubs/${club.id}`)}
 * />
 * 
 * @example
 * // Public variant (discoverable club, user not a member)
 * <ClubCard
 *   variant="public"
 *   club={{ id: '2', name: 'Other Club', location: 'Manchester', banner_image_url: null }}
 *   activity={{ hasRecentActivity: true, upcomingCount: 3, nextSession: { date, time, name } }}
 *   onViewClub={() => showClubInfoModal(club)}
 *   onRequestJoin={() => showJoinModal(club)}
 * />
 * 
 * @param {Object} props
 * @param {string} props.variant - Required. One of: 'member' | 'public'
 * @param {Object} props.club - Club data object
 * @param {string} props.club.id - Club ID
 * @param {string} props.club.name - Club name
 * @param {string} [props.club.location] - Club location
 * @param {string|null} [props.club.banner_image_url] - Banner image storage path
 * @param {string|null} [props.club.logo_image_url] - Logo image storage path (shown as circle next to club name)
 * @param {number} [props.club.member_count] - Member count (for public variant)
 * @param {string} [props.club.role] - User's role in club (for member variant)
 * @param {Object} [props.stats] - Personal stats (for member variant)
 * @param {number} [props.stats.mixedElo] - Mixed ELO rating
 * @param {number} [props.stats.levelsElo] - Levels ELO rating
 * @param {number} [props.stats.totalGamesPlayed] - Total games played
 * @param {number|null} [props.clubWinRatePct] - Win rate at this club (member variant)
 * @param {Object|null} [props.leaderboardTeaser] - Live Active rank teaser (member variant)
 * @param {number|null} [props.memberCount] - Club member count (optional meta row)
 * @param {Object[]} [props.admins] - Club admin avatars (member or public variant)
 * @param {boolean} [props.showClubMeta] - Show member count row on member cards
 * @param {'strip'|'compact'} [props.layout] - Card density; compact for clubs page grids
 * @param {boolean} [props.insightsLoading] - Show skeleton while win-rate data loads
 * @param {Object} [props.activity] - Club activity data (for public variant)
 * @param {boolean} [props.activity.hasRecentActivity] - Had session in last 7 days
 * @param {number} [props.activity.upcomingCount] - Number of upcoming sessions in next 7 days
 * @param {Object} [props.activity.nextSession] - Next session info
 * @param {Function} [props.onClick] - Click handler (for member variant)
 * @param {Function} [props.onViewClub] - Handler for "View Club" button (for public variant, non-members)
 * @param {Function} [props.onRequestJoin] - Handler for "Request Join" button (for public variant, non-members)
 * @param {boolean} [props.disabled] - Disable card interaction
 * @param {string} [props.className] - Additional CSS classes
 * @param {boolean} [props.hasPendingEmailRequest] - Whether user has a pending email assignment request
 */
const ClubCard = ({
  variant,
  club,
  stats = null,
  activity = null, // { hasRecentActivity, upcomingCount, nextSession: { date, time, name } }
  clubWinRatePct = null,
  leaderboardTeaser = null,
  memberCount = null,
  admins = null,
  showClubMeta = false,
  layout = 'strip',
  insightsLoading = false,
  onClick = null,
  onViewClub = null,
  onRequestJoin = null,
  onWithdraw = null,
  onRequestReactivate = null,
  onWithdrawReactivate = null,
  disabled = false,
  className = '',
  membershipStatus = null, // { status: 'member' | 'pending' | 'rejected' | 'can_join' | 'inactive', message: string }
  hasPendingEmailRequest = false,
  isWithdrawing = false,
}) => {
  // Validate variant
  const validVariants = ['member', 'public'];
  if (!validVariants.includes(variant)) {
    console.warn(`ClubCard: Invalid variant "${variant}". Using "member" as fallback.`);
    variant = 'member';
  }

  // Determine if user is a non-member (can join)
  const isNonMember = variant === 'public' && 
    (!membershipStatus || membershipStatus.status === null || membershipStatus.status === 'can_join');
  
  // Check for cooldown or blocked status
  const isCooldown = membershipStatus?.status === 'cooldown' || membershipStatus?.status === 'blocked';
  const isJoinPending = membershipStatus?.status === 'pending';
  const isLinkPending = membershipStatus?.status === 'link_pending';
  const isInactive = membershipStatus?.status === 'inactive' || club.membershipStatus === 'inactive';
  
  // Show buttons if non-member OR if there's a pending/rejected request (to show withdraw button) OR if there's a cooldown (to show message)
  const showActionButtons =
    isNonMember ||
    isJoinPending ||
    isLinkPending ||
    membershipStatus?.status === 'rejected' ||
    isCooldown ||
    isInactive;

  // Variant-specific configuration
  const variantConfig = {
    member: {
      actionLabel: 'View Details →',
      showStats: true,
      showRole: true,
    },
    public: {
      actionLabel: 'View Club →',
      showStats: false,
      showRole: false,
    },
  };

  const config = variantConfig[variant];
  const resolvedAdmins = admins ?? club.admins ?? [];
  const resolvedMemberCount = memberCount ?? club.member_count ?? null;
  const isCompact = layout === 'compact';
  const showPersonalInsights =
    variant === 'member' &&
    club.membershipStatus !== 'inactive' &&
    (clubWinRatePct != null || leaderboardTeaser != null || !stats || insightsLoading);
  const hideCardAction = isCompact && onClick && !showActionButtons;
  const showRoleInHeader = config.showRole && club.role && !isCompact && club.membershipStatus !== 'inactive';
  const showMemberMeta = variant === 'member' && showClubMeta && (resolvedMemberCount != null || resolvedAdmins.length > 0);

  // Show banner if banner_image_url exists (even if null, component handles it)
  const showBanner = true; // Always show banner component (it handles placeholder)
  
  // When banner is shown with name/location overlay, hide them from header
  const showNameInHeader = !showBanner;
  const showLocationInHeader = !showBanner;

  return (
    <ClubCardBase
      variant={variant}
      onClick={showActionButtons ? null : onClick} // Disable card click when showing buttons
      disabled={disabled}
      className={className}
    >
      {showBanner && (
        <div className="relative">
          <ClubBanner
            bannerImageUrl={club.banner_image_url || null}
            clubName={club.name}
            location={club.location}
            logoImageUrl={club.logo_image_url || null}
            variant="card"
          />
          {isCompact && isInactive ? (
            <span className="absolute top-2 right-2 inline-flex items-center rounded-full bg-warning-light px-2 py-0.5 text-[0.625rem] font-semibold text-warning-dark shadow-sm">
              {membershipStatus?.reactivationRequested ? 'Reactivation requested' : 'Inactive'}
            </span>
          ) : isCompact && club.role ? (
            <span
              className={`absolute top-2 right-2 inline-flex items-center rounded-full px-2 py-0.5 text-[0.625rem] font-semibold shadow-sm ${getClubRoleBadgeClass(club.role)}`}
            >
              {getClubRoleLabel(club.role)}
            </span>
          ) : null}
          {variant === 'public' && !(isCompact && isInactive) && (isInactive || isJoinPending || isLinkPending) ? (
            <span
              className={`absolute top-2 right-2 inline-flex items-center rounded-full px-2 py-0.5 text-[0.625rem] font-semibold shadow-sm ${
                isInactive
                  ? 'bg-warning-light text-warning-dark'
                  : isLinkPending
                    ? 'bg-info-light text-info-dark'
                    : 'bg-warning-light text-warning-dark'
              }`}
            >
              {isInactive
                ? (membershipStatus?.reactivationRequested ? 'Reactivation requested' : 'Inactive')
                : isLinkPending
                  ? 'Link requested'
                  : 'Request pending'}
            </span>
          ) : null}
        </div>
      )}

      <div className={showBanner ? (isCompact ? 'p-4 pt-3' : 'p-6 pt-4') : 'p-6'}>
        <ClubCardHeader
          name={showNameInHeader ? club.name : null}
          location={showLocationInHeader ? club.location : null}
          role={showRoleInHeader ? club.role : null}
          variant={variant}
          membershipStatus={membershipStatus}
          hasPendingEmailRequest={hasPendingEmailRequest}
          hasRecentActivity={activity?.hasRecentActivity}
        />

        {showPersonalInsights ? (
          <ClubPersonalInsights
            clubId={club.id}
            clubWinRatePct={clubWinRatePct}
            leaderboardTeaser={leaderboardTeaser}
            layout={layout}
            loading={insightsLoading}
            showLeaderboardLink={!isCompact}
            className={isCompact ? 'pt-2 border-t border-border-subtle' : 'pt-4 border-t border-border'}
          />
        ) : (
          <ClubCardStats
            stats={stats}
            variant={variant}
            memberCount={variant === 'public' ? (resolvedMemberCount ?? null) : null}
            activity={activity}
          />
        )}

        {showMemberMeta ? (
          <ClubCardMetaRow
            memberCount={resolvedMemberCount}
            admins={resolvedAdmins}
            compact={isCompact}
          />
        ) : null}

        {!showMemberMeta && variant === 'public' && resolvedAdmins.length > 0 ? (
          <ClubAdmins admins={resolvedAdmins} />
        ) : null}

        {showActionButtons ? (
          <ClubCardAction
            variant={variant}
            onViewClub={onViewClub}
            onRequestJoin={isNonMember && !isCooldown && !isInactive ? onRequestJoin : null}
            onWithdraw={(membershipStatus?.status === 'pending' || membershipStatus?.status === 'rejected') ? onWithdraw : null}
            onRequestReactivate={isInactive ? onRequestReactivate : null}
            onWithdrawReactivate={isInactive ? onWithdrawReactivate : null}
            showButtons={true}
            isWithdrawing={isWithdrawing}
            membershipStatus={
              isInactive && membershipStatus?.status !== 'inactive'
                ? { status: 'inactive', reactivationRequested: Boolean(membershipStatus?.reactivationRequested) }
                : membershipStatus
            }
          />
        ) : hideCardAction ? null : (
          <ClubCardAction
            label={config.actionLabel}
            variant={variant}
          />
        )}
      </div>
    </ClubCardBase>
  );
};

export default ClubCard;

